import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, saveSession, getStoredUser, isLoggedIn } from '../services/authApi';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      if (isLoggedIn()) {
        try {
          const result = await authApi.validateSession();
          if (result.valid) {
            setUser(result.user);
            setIsAuthenticated(true);
          } else {
            // Invalid session, clear storage
            localStorage.removeItem('sessionToken');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Session validation error:', error);
          localStorage.removeItem('sessionToken');
          localStorage.removeItem('user');
        }
      } else {
        // Check for stored user (may have just logged in)
        const storedUser = getStoredUser();
        if (storedUser) {
          setUser(storedUser);
          setIsAuthenticated(true);
        }
      }
      setIsLoading(false);
    };

    checkSession();
  }, []);

  const signup = useCallback(async (email, password, name) => {
    setAuthError(null);
    try {
      const result = await authApi.signup(email, password, name);
      return result;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Signup failed';
      setAuthError(errorMsg);
      throw new Error(errorMsg);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setAuthError(null);
    try {
      const result = await authApi.login(email, password);
      return result;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Login failed';
      setAuthError(errorMsg);
      throw new Error(errorMsg);
    }
  }, []);

  const verifyOTP = useCallback(async (email, otpCode, otpType) => {
    setAuthError(null);
    try {
      const result = await authApi.verifyOTP(email, otpCode, otpType);

      // If login OTP verified successfully, save session
      if (result.session_token) {
        saveSession(result.session_token, result.user);
        setUser(result.user);
        setIsAuthenticated(true);
      }

      return result;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'OTP verification failed';
      setAuthError(errorMsg);
      throw new Error(errorMsg);
    }
  }, []);

  const resendOTP = useCallback(async (email, otpType) => {
    setAuthError(null);
    try {
      const result = await authApi.resendOTP(email, otpType);
      return result;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to resend OTP';
      setAuthError(errorMsg);
      throw new Error(errorMsg);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);

      // Clear auth data
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('user');

      // Clear portfolio data (allocq_ prefixed keys) but preserve onboarding state
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('allocq_') &&
            !key.startsWith('allocq_onboarding_') &&
            !key.startsWith('allocq_page_onboarding_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }, []);

  const clearError = useCallback(() => {
    setAuthError(null);
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    authError,
    signup,
    login,
    verifyOTP,
    resendOTP,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
